
/**
 * @file 对device信息做相关处理
 */

/**
 * @class 设备抽象类
 */
class Device {
  constructor(userAgent) {
    this.userAgent = userAgent
  }
  /**
   * 返回ua
   * @return {string} 返回ua
   */
  get ua() {
    const ua = this.userAgent || window.navigator.userAgent || '';
    try {
      const event = useRequestEvent();
      return event?.req?.headers["user-agent"] || ua;
    } catch (e) {}
    return ua;
  }

  /**
   * app版本
   * @return {string} app版本号， 未匹配时返回空字符串''
   */
  get appVersion() {
    const str = this.isKy ? 'kwaiying' : this.isYt ? 'm2u' : ''
    const regExp = new RegExp(str + '\\/(\\d+(\\.\\d+){1,2})')
    const matched = this.ua.match(regExp)
    return matched && matched[1] ? matched[1] : ''
  }

  /**
   * 设备平台
   * @return {string} 返回设备平台 ios | android | windows
   */
  get system() {
    const matched = this.ua.match(/(iP(hone|ad)|Android|Windows)/)
    if (!matched) {
      return 'other'
    }
    switch (matched[1]) {
      case 'iPhone':
      case 'iPad':
        return 'ios'
      default:
        return matched[1].toLowerCase()
    }
  }

  get isPC() {
    return !this.isIOS && !this.isAndroid
  }

  /**
   * 是否在快影webview中
   * @return {boolean}
   */
  get isWebview() {
    const str = this.isKy ? 'kwaiying' : this.isYt ? 'm2u' : ''
    const regExp = new RegExp(str + '\\/(\\d+(\\.\\d+){1,2})')
    return regExp.test(this.ua)
  }

  /**
   * 是否在快手中
   * @return {boolean}
   */
  get isInKS() {
    return /Kwai\/(\d+(\.\d+){1,2})/.test(this.ua)
  }

  /**
   * 是否在快手极速版中
   * @return {boolean}
   */
  get isInKSJS() {
    return /ksnebula\/(\d+(\.\d+){1,2})/i.test(this.ua)
  }

  /**
   * 浏览器信息
   * @return {string} wx | qq | isQQbrowser | ohter
   */
  get browser() {
    if (this.isWexin) {
      return 'wx'
      // eslint-disable-next-line no-else-return
    } else if (this.isQQ) {
      return 'qq'
    } else if (this.isQQbrowser) {
      return 'qqBrowser'
    } else if (this.isKy) {
      return 'ky'
    } else if (this.isWeibo) {
      return 'weibo'
    } else if (this.isInKS) {
      return 'ks'
    } else if (this.isYt) {
      return 'm2u'
    } else {
      return 'other'
    }
  }

  /**
   * 是否ie浏览器
   * @return {boolean}
   */
  get isIE() {
    return !!window.ActiveXObject || 'ActiveXObject' in window
  }

  /**
   * 判断是否qq浏览器
   * @return {boolean}
   */
  get isQQbrowser() {
    return /MQQBrowser/i.test(this.ua)
  }

  /**
   * 判断是否夸克浏览器
   * @return {boolean}
   */
  get isQuarkbrowser() {
    return /Quark/i.test(this.ua)
  }

  /**
   * 是否微信环境
   * @return {boolean}
   */
  get isWexin() {
    return /MicroMessenger/i.test(this.ua)
  }

  /**
   * 是否微博环境
   * @return {boolean}
   */
  get isWeibo() {
    return /WeiBo/i.test(this.ua)
  }

  /**
   * 是否快影环境
   * @return {boolean}
   */
  get isKy() {
    return /kwaiying/i.test(this.ua)
  }

  get inKwaiying() {
    return /kwaiying/i.test(this.ua)
  }

  /**
   * 是否一甜
   * @return {boolean}
   */
  get isYt() {
    return /m2u/i.test(this.ua)
  }

  /**
   * 是否QQ
   * @return {boolean}
   */
  get isQQ() {
    return / QQ\//i.test(this.ua)
  }

  /**
   * 是否ios
   * @return {boolean}
   */
  get isIOS() {
    return this.system === 'ios'
  }

  /**
   * 是否安卓
   * @return {boolean}
   */
  get isAndroid() {
    return this.system === 'android'
  }

  /**
   * 是否鸿蒙
   * @return {boolean}
   */
  get isHarmony() {
    return /Harmony/i.test(this.ua)
  }

  /**
   * 获得安卓版本
   * @return {string}
   */
  get androidVersion() {
    if (this.system !== 'android') {
      return ''
    }
    const matched = this.ua.match(/Android\s+(\d)[.\d]{0,3}/i)
    return matched && matched[1] ? matched[1] : ''
  }

  /**
   * 获得ios版本号
   * @returns {string}
   */
  get iosVersion() {
    if (this.system !== 'ios') {
      return ''
    }
    const matched = this.ua.match(/cpu\s+iphone\s+os\s+([\d_]+)/i)
    if (matched && matched[1]) {
      return matched[1].replace(/_/g, '.')
    }
    return ''
  }

  /**
   * 判断是否andorid 8；
   * @return {boolean}
   */
  get isAndroid8() {
    return this.system === 'android' && +this.androidVersion > 8
  }

  get model() {
    if (this.isIOS) {
      return 'iPhone'
    } else if (this.isAndroid) {
      const matched = this.ua.match(
        /android\s+[\d.]+?;\s+(.+?)(\)\s+applewebkit|;)/i
      )
      return matched ? matched[1] : ''
    }
    return ''
  }
}

export { Device }

export default new Device()
